import {
  For,
  Show,
  createEffect,
  createSignal,
  onCleanup,
} from "solid-js";
import { A, useNavigate } from "@solidjs/router";
import { api } from "../api/client";
import type {
  AdminUserDetail,
  AdminUserRow,
  PlatformStats,
} from "../api/types";
import PageHero from "../components/PageHero";
import Cell from "../components/tg/Cell";
import Section from "../components/tg/Section";
import { t } from "../i18n";
import { tgAlert, tgConfirm } from "../lib/telegram";
import { useAuth } from "../stores/auth";

function StatCard(props: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <Cell
      title={props.label}
      subtitle={props.hint}
      value={String(props.value)}
    />
  );
}

function userLabel(u: { firstName: string; lastName: string | null; username: string | null }) {
  const name = [u.firstName, u.lastName].filter(Boolean).join(" ");
  return u.username ? `${name} (@${u.username})` : name;
}

export default function AdminPage() {
  const auth = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = createSignal<PlatformStats | null>(null);
  const [users, setUsers] = createSignal<AdminUserRow[]>([]);
  const [total, setTotal] = createSignal(0);
  const [page, setPage] = createSignal(1);
  const [q, setQ] = createSignal("");
  const [filterAdmin, setFilterAdmin] = createSignal<"all" | "yes" | "no">(
    "all",
  );
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<string | null>(null);

  const [selectedId, setSelectedId] = createSignal<string | null>(null);
  const [detail, setDetail] = createSignal<AdminUserDetail | null>(null);
  const [detailLoading, setDetailLoading] = createSignal(false);
  const [actionBusy, setActionBusy] = createSignal(false);

  // Gate: non-admins leave immediately
  createEffect(() => {
    if (auth.loading()) return;
    const p = auth.profile();
    if (!p) return;
    if (!p.isAdmin) {
      navigate("/profile", { replace: true });
    }
  });

  async function loadStats() {
    const s = await api.adminStats();
    setStats(s);
  }

  async function loadUsers() {
    setLoading(true);
    setError(null);
    try {
      const isAdmin =
        filterAdmin() === "yes"
          ? true
          : filterAdmin() === "no"
          ? false
          : undefined;
      const res = await api.adminListUsers({
        q: q().trim() || undefined,
        page: page(),
        pageSize: 30,
        isAdmin,
      });
      setUsers(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("ERROR_GENERIC"));
      if (String(err).includes("403") || String(err).includes("Forbidden") || String(err).includes("Admin")) {
        navigate("/profile", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }

  createEffect(() => {
    if (!auth.profile()?.isAdmin) return;
    page();
    filterAdmin();
    let cancelled = false;
    (async () => {
      try {
        await Promise.all([loadStats(), loadUsers()]);
      } catch {
        /* loadUsers sets error */
      }
      if (cancelled) return;
    })();
    onCleanup(() => {
      cancelled = true;
    });
  });

  // Search with debounce on q change (reset page)
  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  function onSearchInput(value: string) {
    setQ(value);
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setPage(1);
      void loadUsers();
    }, 300);
  }
  onCleanup(() => clearTimeout(searchTimer));

  async function openUser(id: string) {
    setSelectedId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await api.adminGetUser(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("ERROR_GENERIC"));
    } finally {
      setDetailLoading(false);
    }
  }

  async function toggleAdmin(u: AdminUserRow | AdminUserDetail, next: boolean) {
    if (actionBusy()) return;
    const me = auth.profile()?.id;
    if (!next && u.id === me) {
      await tgAlert(t("ADMIN_CANNOT_SELF"));
      return;
    }
    if (
      !(await tgConfirm(
        next ? t("ADMIN_GRANT_CONFIRM") : t("ADMIN_REVOKE_CONFIRM"),
      ))
    ) {
      return;
    }
    setActionBusy(true);
    try {
      const updated = await api.adminSetUserAdmin(u.id, next);
      setUsers((list) =>
        list.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)),
      );
      if (detail()?.id === updated.id) {
        setDetail({ ...detail()!, ...updated });
      }
      await loadStats();
    } catch (err) {
      await tgAlert(err instanceof Error ? err.message : t("ERROR_GENERIC"));
    } finally {
      setActionBusy(false);
    }
  }

  const pages = () => Math.max(1, Math.ceil(total() / 30));

  return (
    <div class="pb-6">
      <PageHero
        title={t("ADMIN_PANEL")}
        aside={
          <A href="/profile" class="btn-ghost px-2">
            {t("BACK")}
          </A>
        }
      />

      <Show when={error()}>
        <p class="mb-3 px-1 text-[15px] text-[var(--destructive)]">{error()}</p>
      </Show>

      <Show when={stats()}>
        {(s) => (
          <Section header={t("ADMIN_OVERVIEW")}>
            <StatCard
              label={t("ADMIN_USERS")}
              value={s().users}
              hint={`+${s().newUsersLast7Days} / 7d`}
            />
            <StatCard label={t("ADMIN_ADMINS")} value={s().admins} />
            <StatCard
              label={t("ADMIN_COMPLETED")}
              value={s().completedWorkouts}
              hint={`${s().workoutsLast7Days} / 7d · ${s().workoutsLast30Days} / 30d`}
            />
            <StatCard
              label={t("ADMIN_PLANNED")}
              value={s().plannedWorkouts}
              hint={`${s().openPlanned} ${t("ADMIN_OPEN_PLANS")}`}
            />
            <StatCard label={t("TEMPLATES")} value={s().templates} />
            <StatCard label={t("CATALOG")} value={s().catalogExercises} />
            <StatCard
              label={t("BODY_WEIGHT")}
              value={s().bodyWeightLogs}
              hint={t("ADMIN_WEIGHT_LOGS")}
            />
            <StatCard
              label={t("ADMIN_NEW_USERS_30D")}
              value={s().newUsersLast30Days}
            />
          </Section>
        )}
      </Show>

      <Section>
        <div class="px-4 py-2">
          <input
            class="input"
            type="search"
            placeholder={t("ADMIN_SEARCH_USERS")}
            value={q()}
            onInput={(e) => onSearchInput(e.currentTarget.value)}
          />
        </div>
      </Section>

      <div class="tg-segmented mb-3" role="tablist">
        {(
          [
            ["all", "FILTER_ALL"],
            ["yes", "ADMIN_FILTER_ADMINS"],
            ["no", "ADMIN_FILTER_USERS"],
          ] as const
        ).map(([key, label]) => (
          <button
            type="button"
            role="tab"
            aria-selected={filterAdmin() === key}
            onClick={() => {
              setFilterAdmin(key);
              setPage(1);
            }}
          >
            {t(label)}
          </button>
        ))}
      </div>

      <Show when={loading()}>
        <p class="text-center text-[15px] text-ink-muted">{t("LOADING")}</p>
      </Show>

      <Show when={!loading() && users().length === 0}>
        <div class="empty-state">
          <p class="empty-state-hint">{t("NO_DATA")}</p>
        </div>
      </Show>

      <Section header={t("ADMIN_USERS")}>
        <For each={users()}>
          {(u) => (
            <Cell
              title={userLabel(u)}
              subtitle={`tg:${u.telegramId} · ${u.language} · ${u.completedCount} ${t("COMPLETED").toLowerCase()} · ${u.plannedCount} ${t("PLANNED").toLowerCase()}`}
              value={u.isAdmin ? "admin" : undefined}
              chevron
              onClick={() => openUser(u.id)}
            />
          )}
        </For>
      </Section>

      <Show when={pages() > 1}>
        <div class="flex items-center justify-between gap-2 pb-4">
          <button
            type="button"
            class="btn-secondary !h-11 px-4 text-[15px]"
            disabled={page() <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ‹
          </button>
          <span class="text-[13px] text-ink-muted">
            {page()} / {pages()} · {total()}
          </span>
          <button
            type="button"
            class="btn-secondary !h-11 px-4 text-[15px]"
            disabled={page() >= pages()}
            onClick={() => setPage((p) => p + 1)}
          >
            ›
          </button>
        </div>
      </Show>

      <Show when={selectedId()}>
        <Section
          header={t("ADMIN_USER_DETAIL")}
          footer={undefined}
        >
          <Cell
            title={t("CLOSE")}
            onClick={() => {
              setSelectedId(null);
              setDetail(null);
            }}
          />
        </Section>

        <Show when={detailLoading()}>
          <p class="text-[15px] text-ink-muted">{t("LOADING")}</p>
        </Show>

        <Show when={detail()}>
          {(d) => (
            <>
              <Section>
                <Cell
                  title={userLabel(d())}
                  subtitle={`${t("ADMIN_JOINED")}: ${new Date(d().createdAt).toLocaleString()}`}
                  value={`tg:${d().telegramId}`}
                />
                <Cell title={t("COMPLETED")} value={String(d().completedCount)} />
                <Cell title={t("PLANNED")} value={String(d().plannedCount)} />
              </Section>
              <div class="mb-4">
                <button
                  type="button"
                  class={d().isAdmin ? "btn-danger w-full" : "btn-primary w-full"}
                  disabled={actionBusy()}
                  onClick={() => toggleAdmin(d(), !d().isAdmin)}
                >
                  {d().isAdmin ? t("ADMIN_REVOKE") : t("ADMIN_GRANT")}
                </button>
              </div>

              <Show when={d().recentCompleted.length > 0}>
                <Section header={t("ADMIN_RECENT_COMPLETED")}>
                  <For each={d().recentCompleted}>
                    {(w) => (
                      <Cell
                        title={w.title}
                        value={new Date(w.performedAt).toLocaleDateString()}
                      />
                    )}
                  </For>
                </Section>
              </Show>

              <Show when={d().recentPlanned.length > 0}>
                <Section header={t("ADMIN_RECENT_PLANNED")}>
                  <For each={d().recentPlanned}>
                    {(w) => (
                      <Cell title={w.title} value={w.plannedFor} />
                    )}
                  </For>
                </Section>
              </Show>
            </>
          )}
        </Show>
      </Show>
    </div>
  );
}
